import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { io } from 'socket.io-client';
import axios from 'axios';
import { initialize } from '@telnyx/video';
import { AuthContext } from '../contexts/AuthContext';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

// ── Tiny SVG icon helper ─────────────────────────────────────────────────────
function Ico({ children, className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden="true">{children}</svg>
  );
}
const VideoIcon    = ({ className }) => <Ico className={className}><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></Ico>;
const VideoOffIcon = ({ className }) => <Ico className={className}><line x1="1" y1="1" x2="23" y2="23"/><path d="M15 11.08V11a4 4 0 0 0-5.93-3.5"/><path d="M9.1 9.09A4 4 0 0 0 9 10v1H5"/><path d="M16 19H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h3l14 14"/><path d="M23 7l-7 5 7 5V7z"/></Ico>;
const MicIcon      = ({ className }) => <Ico className={className}><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></Ico>;
const MicOffIcon   = ({ className }) => <Ico className={className}><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></Ico>;
const PhoneOffIcon = ({ className }) => <Ico className={className}><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.42 19.42 0 0 1 3.43 9.37"/><line x1="23" y1="1" x2="1" y2="23"/></Ico>;
const UserIcon     = ({ className }) => <Ico className={className}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></Ico>;
const RefreshIcon  = ({ className }) => <Ico className={className}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.67"/></Ico>;
const LinkIcon     = ({ className }) => <Ico className={className}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></Ico>;
const CalIcon      = ({ className }) => <Ico className={className}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></Ico>;
const PlusIcon     = ({ className }) => <Ico className={className}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></Ico>;
const TrashIcon    = ({ className }) => <Ico className={className}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></Ico>;

// ── Avatar ───────────────────────────────────────────────────────────────────
function Avatar({ label, size = 'lg', isDark }) {
  const sz = size === 'lg' ? 'h-24 w-24 text-3xl' : size === 'md' ? 'h-16 w-16 text-xl' : 'h-12 w-12 text-base';
  const initials = label
    ? label.split(/[@.\s]/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('')
    : '?';
  return (
    <div className={`${sz} rounded-full flex items-center justify-center font-bold select-none ${
      isDark ? 'bg-sky-500/20 text-sky-300' : 'bg-blue-100 text-blue-700'
    }`}>
      {initials}
    </div>
  );
}

// ── Grid sizing based on participant count ───────────────────────────────────
function gridClasses(count) {
  if (count <= 1) return 'grid-cols-1';
  if (count <= 2) return 'grid-cols-1 md:grid-cols-2';
  if (count <= 4) return 'grid-cols-2';
  if (count <= 6) return 'grid-cols-2 md:grid-cols-3';
  return 'grid-cols-3 md:grid-cols-4';
}

// ── ParticipantTile — attaches a MediaStream to a <video>/<audio> element ────
// `mediaStream` is composed by the caller from the SDK's per-participant tracks
// (audioTrack / videoTrack from room.getParticipantStream(...)). For the local
// "self" tile, mediaStream is the getUserMedia stream directly.
function ParticipantTile({ id, label, mediaStream, hasVideo = true, isDark, isLocal = false }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const vEl = videoRef.current;
    const aEl = audioRef.current;
    if (vEl) vEl.srcObject = mediaStream || null;
    if (aEl && !isLocal) aEl.srcObject = mediaStream || null;
  }, [mediaStream, isLocal]);

  return (
    <div className={`relative rounded-2xl overflow-hidden border ${isDark ? 'bg-black border-white/10' : 'bg-slate-900 border-slate-200'} aspect-video`}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal}
        className="w-full h-full object-cover"
        style={isLocal ? { transform: 'scaleX(-1)' } : undefined}
      />
      {!isLocal && <audio ref={audioRef} autoPlay playsInline />}
      {/* fallback avatar shown when video is off or stream not yet attached */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {(!hasVideo || !mediaStream) && (
          <Avatar label={label} size="md" isDark />
        )}
      </div>
      <div className="absolute bottom-2 left-2 px-2.5 py-1 rounded-lg bg-black/60 backdrop-blur text-white text-xs font-medium">
        {label}{isLocal ? ' (You)' : ''}
      </div>
      <span className="sr-only" data-participant-id={id} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function VideoCall({ theme = 'dark' }) {
  const { user } = useContext(AuthContext);
  const isDark = theme === 'dark';

  // ── State ─────────────────────────────────────────────────────────────────
  const [callState, setCallState] = useState('idle'); // idle | calling | incoming | connected
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [incomingInvite, setIncomingInvite] = useState(null); // { fromUserId, fromEmail, slug, title, roomId }
  const [upcoming, setUpcoming] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null); // { slug, title, roomId }
  const [pendingInvite, setPendingInvite] = useState(null); // { toUserId, toEmail, slug }
  // remotes: { [participantId]: { id, label, mediaStream, hasVideo } }
  const [remotes, setRemotes] = useState({});
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [error, setError] = useState('');
  const [copiedSlug, setCopiedSlug] = useState('');
  const [deletingRoomId, setDeletingRoomId] = useState(null); // id being confirmed for delete
  const [cameraStatus, setCameraStatus] = useState(null); // null | 'checking' | 'ok' | error string

  // ── Schedule-call modal state ─────────────────────────────────────────────
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    title: '', scheduledAt: '', emails: '', maxParticipants: 10,
  });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState('');

  // ── Refs ──────────────────────────────────────────────────────────────────
  const socketRef      = useRef(null);
  const roomRef        = useRef(null);  // @telnyx/video Room instance
  const localStreamRef = useRef(null);
  const localSelfRef   = useRef(null);  // synthetic "self" participant for local tile
  const localPidRef    = useRef(null);  // Telnyx-assigned local participant ID (so we can ignore self echoes)
  const subscribedRef  = useRef(new Set()); // `${pid}|${key}` pairs we've already subscribed to (or attempted)

  // ── Theme tokens ──────────────────────────────────────────────────────────
  const text      = isDark ? 'text-white'     : 'text-slate-900';
  const muted     = isDark ? 'text-white/50'  : 'text-slate-500';
  const faint     = isDark ? 'text-white/30'  : 'text-slate-400';
  const surface   = isDark ? 'bg-white/5'     : 'bg-slate-50';
  const border    = isDark ? 'border-white/10': 'border-slate-200';

  // ── Auth headers ──────────────────────────────────────────────────────────
  const authHeaders = useCallback(() => {
    const token =
      sessionStorage.getItem('token') ||
      sessionStorage.getItem('pbx_token') ||
      localStorage.getItem('pbx_token') ||
      localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // ── Socket setup ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;

    const socket = io(`${SOCKET_URL}/video`, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });

    socket.on('connect', () => {
      setSocketConnected(true);
      setError('');
      socket.emit('register', {
        userId: user.id,
        email: user.email,
        tenantId: user.tenantId,
        type: 'video',
      });
    });

    socket.on('disconnect', () => setSocketConnected(false));
    socket.on('connect_error', (err) => {
      setError(`Connection error: ${err.message}`);
      setSocketConnected(false);
    });

    socket.on('registered', () => socket.emit('get-online-users'));
    socket.on('online-users', (users) => setOnlineUsers(users));

    // New video-rooms events
    socket.on('video-room-invite', (payload) => {
      setIncomingInvite(payload);
      setCallState('incoming');
    });
    socket.on('video-room-cancelled', () => {
      setIncomingInvite(null);
      setCallState('idle');
    });
    socket.on('video-room-declined', ({ slug }) => {
      if (pendingInvite?.slug === slug) {
        setError('Call was declined.');
        leaveRoom();
      }
    });

    socketRef.current = socket;
    return () => {
      socket.disconnect();
      // don't call leaveRoom here — let unmount happen naturally; Room.disconnect()
      // handled by the active-call effect below.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Load upcoming scheduled rooms ────────────────────────────────────────
  const loadUpcoming = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/video-rooms/upcoming`, {
        headers: authHeaders(),
      });
      setUpcoming(Array.isArray(data) ? data : []);
    } catch (e) {
      // non-fatal
    }
  }, [authHeaders]);

  useEffect(() => {
    if (user?.id) loadUpcoming();
  }, [user, loadUpcoming]);

  // ── Camera pre-check — uses enumerateDevices (no permission prompt) ────────
  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    setCameraStatus('checking');
    navigator.mediaDevices.enumerateDevices()
      .then((devices) => {
        const hasCamera = devices.some((d) => d.kind === 'videoinput');
        if (!hasCamera) {
          setCameraStatus('No camera device found');
          return;
        }
        // Device exists — check if permission is granted by looking at label visibility.
        // Labels are empty strings when permission hasn't been granted yet.
        const cam = devices.find((d) => d.kind === 'videoinput');
        if (cam && cam.label) {
          setCameraStatus('ok');
        } else {
          // Device present but no label = permission not yet granted (ask to allow)
          setCameraStatus('Permission needed — click the camera icon in your address bar and allow access, then reload');
        }
      })
      .catch(() => setCameraStatus('Could not enumerate devices'));
  }, []);

  // Poll upcoming rooms every 30 s so the "Join" button activates automatically.
  useEffect(() => {
    if (!user?.id) return undefined;
    const id = setInterval(() => loadUpcoming(), 30000);
    return () => clearInterval(id);
  }, [user, loadUpcoming]);

  // ── Telnyx Room — join with client token + Telnyx roomId ─────────────────
  // NOTE on @telnyx/video@1.0.2 API:
  //   - Construct via initialize({ roomId, clientToken }) — returns Promise<Room>
  //   - Events deliver (participantId, ..., state) — NOT participant objects
  //   - Remote tracks need explicit room.addSubscription(); they don't auto-flow
  //   - Read remote media via room.getParticipantStream(id, key) → audioTrack/videoTrack
  const joinTelnyxRoom = useCallback(async (clientToken, telnyxRoomId, roomMeta) => {
    try {
      if (!telnyxRoomId) {
        throw new Error('Missing telnyxRoomId from join response');
      }

      // Acquire local media first so the "self" tile renders immediately.
      // Attempt video+audio; fall back to audio-only if no video device is found.
      let localStream;
      try {
        localStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch (mediaErr) {
        const name = mediaErr?.name || '';
        if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
          // No camera — try audio only so the call can still proceed.
          try {
            localStream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
            setVideoOff(true);
          } catch {
            throw new Error('No camera or microphone found. Check your device connections and try again.');
          }
        } else if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          throw new Error('Camera/microphone access was denied. Allow access in your browser settings and retry.');
        } else {
          throw new Error(`Could not access camera/microphone: ${mediaErr.message}`);
        }
      }
      localStreamRef.current = localStream;
      localSelfRef.current = {
        id: 'self',
        label: user?.email || 'You',
        mediaStream: localStream,
        hasVideo: true,
      };

      const room = await initialize({
        roomId: telnyxRoomId,
        clientToken,
        enableMessages: false,
      });
      roomRef.current = room;

      // Helper: extract a human label from a participant's context string.
      // Backend encodes context as JSON: {"displayName":"alice@example.com"}.
      // Falls back to plain string, then to 'Participant'.
      const labelFromState = (state, participantId) => {
        try {
          const p = state?.participants?.[participantId]
            || state?.participants?.get?.(participantId);
          const ctx = p?.context;
          if (!ctx) return null;
          if (typeof ctx === 'string') {
            try {
              const parsed = JSON.parse(ctx);
              return parsed?.displayName || ctx || null;
            } catch {
              return ctx || null;
            }
          }
          if (typeof ctx === 'object') return ctx.displayName || null;
        } catch { /* ignore */ }
        return null;
      };

      // Look up a participant entry from state.participants — handle BOTH
      // Map-based and plain-object-based shapes (Telnyx SDK has shipped both).
      const lookupParticipant = (state, pid) => {
        const participants = state?.participants;
        if (!participants) return null;
        if (typeof participants.get === 'function') return participants.get(pid) || null;
        return participants[pid] || null;
      };

      // True if this event refers to the local participant. Combines THREE
      // signals because the Telnyx Video v1 SDK exposes the local id
      // inconsistently across versions:
      //   1. state.participants[id].origin === 'local' (most reliable)
      //   2. cached localPidRef captured after connect
      //   3. participant id matches anything we cached on first sighting
      const isSelf = (participantId, state) => {
        if (localPidRef.current && participantId === localPidRef.current) return true;
        const p = lookupParticipant(state, participantId);
        if (p && (p.origin === 'local' || p.isLocal === true)) {
          // Cache it now so future events without state still match.
          if (!localPidRef.current) localPidRef.current = participantId;
          return true;
        }
        return false;
      };

      // Compare a Telnyx-returned stream's tracks against our published local
      // tracks — if they share a track id, this stream IS our own (the SDK
      // round-tripped our own publish back to us).
      const isOwnStream = (stream) => {
        const localStream = localStreamRef.current;
        if (!stream || !localStream) return false;
        const ourIds = new Set(localStream.getTracks().map((t) => t.id));
        if (stream.audioTrack && ourIds.has(stream.audioTrack.id)) return true;
        if (stream.videoTrack && ourIds.has(stream.videoTrack.id)) return true;
        return false;
      };

      // Helper: build/refresh a MediaStream for a remote participant from
      // whatever tracks the SDK currently exposes for the given stream key.
      const refreshRemote = (participantId, key, state) => {
        if (isSelf(participantId, state)) return;
        const stream = room.getParticipantStream(participantId, key);
        if (!stream) return;
        if (isOwnStream(stream)) {
          // Belt: even if isSelf failed, the track-id check caught a self echo.
          if (!localPidRef.current) localPidRef.current = participantId;
          return;
        }
        const ms = new MediaStream();
        if (stream.audioTrack) ms.addTrack(stream.audioTrack);
        if (stream.videoTrack) ms.addTrack(stream.videoTrack);
        const ctxLabel = labelFromState(state, participantId);
        setRemotes((prev) => ({
          ...prev,
          [participantId]: {
            id: participantId,
            label: ctxLabel || prev[participantId]?.label || 'Participant',
            mediaStream: ms,
            hasVideo: !!stream.videoTrack && stream.isVideoEnabled,
          },
        }));
      };

      room.on('participant_joined', (participantId, state) => {
        if (isSelf(participantId, state)) return;
        const ctxLabel = labelFromState(state, participantId);
        setRemotes((prev) =>
          prev[participantId]
            ? {
                ...prev,
                [participantId]: {
                  ...prev[participantId],
                  label: ctxLabel || prev[participantId].label,
                },
              }
            : {
                ...prev,
                [participantId]: {
                  id: participantId,
                  label: ctxLabel || 'Participant',
                  mediaStream: null,
                  hasVideo: false,
                },
              },
        );
      });

      room.on('participant_left', (participantId, state) => {
        if (isSelf(participantId, state)) return;
        setRemotes((prev) => {
          const next = { ...prev };
          delete next[participantId];
          return next;
        });
      });

      // When a remote publishes a stream, subscribe to receive their tracks.
      // Skip self — we render the local tile directly from getUserMedia.
      room.on('stream_published', async (participantId, key, state) => {
        if (isSelf(participantId, state)) return;
        try {
          await room.addSubscription(participantId, key, {
            audio: true,
            video: true,
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('addSubscription failed', participantId, key, err);
        }
      });

      room.on('stream_unpublished', (participantId, key, state) => {
        if (isSelf(participantId, state)) return;
        setRemotes((prev) => {
          const cur = prev[participantId];
          if (!cur) return prev;
          return { ...prev, [participantId]: { ...cur, mediaStream: null, hasVideo: false } };
        });
      });

      // Once subscription is live, fetch the actual tracks and wire to a stream.
      // Tracks may not be immediately attached to the SDK stream object, so we
      // also retry at 350ms and 1200ms in case getParticipantStream returns null
      // on the first call (e.g. when joining a room where the other side was
      // already streaming — track_enabled won't re-fire for pre-existing tracks).
      room.on('subscription_started', (participantId, key, state) => {
        if (isSelf(participantId, state)) return;
        refreshRemote(participantId, key, state);
        setTimeout(() => {
          try {
            const r = roomRef.current;
            if (r) refreshRemote(participantId, key, r.getState?.());
          } catch { /* ignore */ }
        }, 350);
        setTimeout(() => {
          try {
            const r = roomRef.current;
            if (r) refreshRemote(participantId, key, r.getState?.());
          } catch { /* ignore */ }
        }, 1200);
      });
      room.on('subscription_reconfigured', (participantId, key, state) => {
        if (isSelf(participantId, state)) return;
        refreshRemote(participantId, key, state);
      });
      room.on('track_enabled', (participantId, key, kind, state) => {
        if (isSelf(participantId, state)) return;
        if (kind === 'video') {
          setRemotes((prev) =>
            prev[participantId]
              ? { ...prev, [participantId]: { ...prev[participantId], hasVideo: true } }
              : prev,
          );
        }
        refreshRemote(participantId, key, state);
      });
      room.on('track_disabled', (participantId, key, kind, state) => {
        if (isSelf(participantId, state)) return;
        if (kind === 'video') {
          setRemotes((prev) =>
            prev[participantId]
              ? { ...prev, [participantId]: { ...prev[participantId], hasVideo: false } }
              : prev,
          );
        }
      });

      room.on('disconnected', (/* reason, state */) => {
        leaveRoom();
      });

      // Reconcile remote tiles from a state snapshot. Telnyx Video v1 SDK
      // is state_changed-driven; per-entity events do not replay for
      // participants who were already in the room when we joined.
      const reconcileFromState = async (state) => {
        try {
          const r = roomRef.current;
          if (!r || !state) return;
          if (!localPidRef.current) {
            try {
              const lp = r.getLocalParticipant?.();
              if (lp?.id) localPidRef.current = lp.id;
            } catch { /* ignore */ }
          }
          const partsRaw = state.participants;
          const entries = partsRaw && typeof partsRaw.entries === 'function'
            ? Array.from(partsRaw.entries())
            : Object.entries(partsRaw || {});
          const selfId = localPidRef.current;
          const seen = new Set();
          for (const [pid, p] of entries) {
            if (!pid) continue;
            if (pid === selfId) continue;
            if (p && (p.origin === 'local' || p.isLocal === true)) {
              if (!localPidRef.current) localPidRef.current = pid;
              continue;
            }
            seen.add(pid);
            let displayName = null;
            try {
              const ctx = p?.context;
              if (typeof ctx === 'string') {
                try { displayName = (JSON.parse(ctx) || {}).displayName || null; }
                catch { displayName = ctx || null; }
              } else if (ctx && typeof ctx === 'object') {
                displayName = ctx.displayName || null;
              }
            } catch { /* ignore */ }
            const streams = p?.streams;
            const streamEntries = streams && typeof streams.entries === 'function'
              ? Array.from(streams.entries())
              : Object.entries(streams || {});
            for (const [key /*, s */] of streamEntries) {
              const subKey = `${pid}|${key}`;
              if (!subscribedRef.current.has(subKey)) {
                subscribedRef.current.add(subKey);
                try {
                  await r.addSubscription(pid, key, { audio: true, video: true });
                } catch (err) {
                  const msg = String(err?.message || '').toLowerCase();
                  if (!msg.includes('already')) {
                    subscribedRef.current.delete(subKey);
                    // eslint-disable-next-line no-console
                    console.warn('addSubscription via reconcile failed', pid, key, err);
                  }
                }
              }
              try {
                const stream = r.getParticipantStream?.(pid, key);
                if (stream && (stream.audioTrack || stream.videoTrack)) {
                  if (isOwnStream(stream)) {
                    if (!localPidRef.current) localPidRef.current = pid;
                    continue;
                  }
                  const ms = new MediaStream();
                  if (stream.audioTrack) ms.addTrack(stream.audioTrack);
                  if (stream.videoTrack) ms.addTrack(stream.videoTrack);
                  setRemotes((prev) => ({
                    ...prev,
                    [pid]: {
                      id: pid,
                      label: displayName || prev[pid]?.label || 'Participant',
                      mediaStream: ms,
                      hasVideo: !!stream.videoTrack && stream.isVideoEnabled !== false,
                    },
                  }));
                  continue;
                }
              } catch { /* ignore */ }
            }
            setRemotes((prev) => prev[pid] ? (
              displayName && prev[pid].label !== displayName
                ? { ...prev, [pid]: { ...prev[pid], label: displayName } }
                : prev
            ) : ({
              ...prev,
              [pid]: { id: pid, label: displayName || 'Participant', mediaStream: null, hasVideo: false },
            }));
          }
          setRemotes((prev) => {
            let changed = false;
            const next = {};
            for (const [pid, tile] of Object.entries(prev)) {
              if (seen.has(pid)) next[pid] = tile;
              else changed = true;
            }
            return changed ? next : prev;
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('reconcileFromState failed', err);
        }
      };

      // state_changed is the source of truth — fires on every roster/stream change.
      room.on('state_changed', (state) => { reconcileFromState(state); });

      await room.connect();

      // Capture self pid via the dedicated accessor.
      try {
        const lp = room.getLocalParticipant?.();
        if (lp?.id) localPidRef.current = lp.id;
      } catch { /* ignore */ }

      // Publish local tracks. Note: addStream takes individual MediaStreamTracks,
      // NOT a MediaStream.
      await room.addStream('self', {
        audio: localStream.getAudioTracks()[0],
        video: localStream.getVideoTracks()[0],
      });

      if (!localPidRef.current) {
        try {
          const lp = room.getLocalParticipant?.();
          if (lp?.id) localPidRef.current = lp.id;
        } catch { /* ignore */ }
      }

      // Drop any stale "remote" entry for ourselves and force one reconcile
      // in case state_changed already fired before our handler attached.
      if (localPidRef.current) {
        const selfId = localPidRef.current;
        setRemotes((prev) => {
          if (!prev[selfId]) return prev;
          const next = { ...prev };
          delete next[selfId];
          return next;
        });
      }
      try { reconcileFromState(room.getState?.()); } catch { /* ignore */ }
      setTimeout(() => { try { reconcileFromState(roomRef.current?.getState?.()); } catch { /* ignore */ } }, 500);
      setTimeout(() => { try { reconcileFromState(roomRef.current?.getState?.()); } catch { /* ignore */ } }, 1500);

      setActiveRoom(roomMeta);
      setCallState('connected');
      setAudioMuted(false);
      setVideoOff(false);
    } catch (e) {
      setError(`Could not join room: ${e?.message || e}`);
      leaveRoom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Leave / cleanup ──────────────────────────────────────────────────────
  // Tears down the Telnyx Room and local media defensively. Safe to call
  // from any error path — even partial init (e.g. connect() rejected after
  // initialize() succeeded). Clears UI state unconditionally so a stuck
  // SDK can't pin us in 'connected'.
  const leaveRoom = useCallback(() => {
    const room = roomRef.current;
    roomRef.current = null;
    if (room) {
      try {
        // Remove all listeners first so async events from a half-torn-down
        // SDK can't repopulate state after we clear it.
        room.removeAllListeners?.();
      } catch { /* ignore */ }
      try {
        // disconnect() returns a Promise in @telnyx/video v1 — fire and
        // forget so caller (often an error handler) doesn't await us.
        const p = room.disconnect?.();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch { /* ignore */ }
    }
    if (localStreamRef.current) {
      try {
        localStreamRef.current.getTracks().forEach((t) => {
          try { t.stop(); } catch { /* ignore */ }
        });
      } catch { /* ignore */ }
      localStreamRef.current = null;
    }
    localSelfRef.current = null;
    localPidRef.current = null;
    setRemotes({});
    setActiveRoom(null);
    setPendingInvite(null);
    setIncomingInvite(null);
    setCallState('idle');
    setAudioMuted(false);
    setVideoOff(false);
    setError((prev) => prev); // no-op, keep last error visible
  }, []);

  // unmount safety
  useEffect(() => {
    return () => {
      if (roomRef.current) { try { roomRef.current.disconnect?.(); } catch {} }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  // ── Actions ──────────────────────────────────────────────────────────────
  // Create a room with NO invitee — just join yourself and share the
  // /join/:slug link from the in-call header. Useful when no one else is
  // online in the tenant or when calling an external/public participant.
  const startInstantRoom = useCallback(async () => {
    setError('');
    try {
      setCallState('calling');
      setPendingInvite(null);
      const { data } = await axios.post(
        `${API_URL}/video-rooms`,
        { title: 'Video call' },
        { headers: authHeaders() },
      );
      const room = data.room;
      const tokenResp = await axios.post(
        `${API_URL}/video-rooms/${room.slug}/join`,
        { displayName: user?.email || 'Host' },
      );
      await joinTelnyxRoom(
        tokenResp.data.token,
        tokenResp.data.telnyxRoomId,
        { slug: room.slug, title: room.title, roomId: room.id },
      );
    } catch (e) {
      setError(`Could not create room: ${e?.response?.data?.message || e.message}`);
      leaveRoom();
    }
  }, [authHeaders, joinTelnyxRoom, leaveRoom, user]);

  const startInstantCall = useCallback(async (targetUserId, targetEmail) => {
    setError('');
    try {
      setCallState('calling');
      setPendingInvite({ toUserId: targetUserId, toEmail: targetEmail, slug: null });

      // 1. Create room + invite target
      const { data } = await axios.post(
        `${API_URL}/video-rooms`,
        {
          title: `Call with ${targetEmail}`,
          targetUserId,
        },
        { headers: authHeaders() },
      );
      const room = data.room;
      setPendingInvite({ toUserId: targetUserId, toEmail: targetEmail, slug: room.slug });

      if (data.invited === 0) {
        setError(`${targetEmail} appears to be offline.`);
      }

      // 2. Mint our own join token & connect
      const tokenResp = await axios.post(
        `${API_URL}/video-rooms/${room.slug}/join`,
        { displayName: user?.email || 'Host' },
      );

      await joinTelnyxRoom(
        tokenResp.data.token,
        tokenResp.data.telnyxRoomId,
        {
          slug: room.slug,
          title: room.title,
          roomId: room.id,
        },
      );
    } catch (e) {
      setError(`Could not start call: ${e?.response?.data?.message || e.message}`);
      leaveRoom();
    }
  }, [authHeaders, joinTelnyxRoom, leaveRoom, user]);

  const acceptInvite = useCallback(async () => {
    if (!incomingInvite) return;
    setError('');
    try {
      const { slug, title, roomId } = incomingInvite;
      const { data } = await axios.post(`${API_URL}/video-rooms/${slug}/join`, {
        displayName: user?.email || 'You',
      });
      setIncomingInvite(null);
      await joinTelnyxRoom(data.token, data.telnyxRoomId, { slug, title, roomId });
    } catch (e) {
      setError(`Could not answer call: ${e?.response?.data?.message || e.message}`);
      leaveRoom();
    }
  }, [incomingInvite, joinTelnyxRoom, leaveRoom, user]);

  const declineInvite = useCallback(() => {
    if (!incomingInvite || !socketRef.current) return;
    socketRef.current.emit('video-room-decline', {
      toUserId: incomingInvite.fromUserId,
      slug: incomingInvite.slug,
    });
    setIncomingInvite(null);
    setCallState('idle');
  }, [incomingInvite]);

  const cancelPending = useCallback(() => {
    if (pendingInvite && socketRef.current && pendingInvite.slug) {
      socketRef.current.emit('video-room-cancel', {
        toUserId: pendingInvite.toUserId,
        slug: pendingInvite.slug,
      });
    }
    leaveRoom();
  }, [pendingInvite, leaveRoom]);

  // Join a scheduled room (same flow as instant — mint token then connect).
  const joinScheduledRoom = useCallback(async (slug, title, roomId) => {
    setError('');
    try {
      setCallState('calling');
      setPendingInvite(null);
      const tokenResp = await axios.post(
        `${API_URL}/video-rooms/${slug}/join`,
        { displayName: user?.email || 'Host' },
      );
      await joinTelnyxRoom(
        tokenResp.data.token,
        tokenResp.data.telnyxRoomId,
        { slug, title, roomId },
      );
    } catch (e) {
      setError(`Could not join room: ${e?.response?.data?.message || e.message}`);
      leaveRoom();
    }
  }, [joinTelnyxRoom, leaveRoom, user]);

  // Submit the schedule-call form.
  const scheduleCall = useCallback(async () => {
    setScheduleError('');
    if (!scheduleForm.title.trim()) { setScheduleError('Title is required.'); return; }
    if (!scheduleForm.scheduledAt) { setScheduleError('Date and time are required.'); return; }
    const scheduledAt = new Date(scheduleForm.scheduledAt);
    if (isNaN(scheduledAt.getTime()) || scheduledAt <= new Date()) {
      setScheduleError('Scheduled time must be a valid date in the future.');
      return;
    }
    const emails = scheduleForm.emails
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    setScheduleLoading(true);
    try {
      await axios.post(
        `${API_URL}/video-rooms/schedule`,
        {
          title: scheduleForm.title.trim(),
          scheduledAt: scheduledAt.toISOString(),
          ...(emails.length > 0 && { participantEmails: emails }),
          maxParticipants: Number(scheduleForm.maxParticipants) || 10,
        },
        { headers: authHeaders() },
      );
      setShowScheduleModal(false);
      setScheduleForm({ title: '', scheduledAt: '', emails: '', maxParticipants: 10 });
      loadUpcoming();
    } catch (e) {
      setScheduleError(e?.response?.data?.message || e?.message || 'Failed to schedule call.');
    } finally {
      setScheduleLoading(false);
    }
  }, [scheduleForm, authHeaders, loadUpcoming]);

  const toggleAudio = () => {
    if (!localStreamRef.current) return;
    const enabled = audioMuted; // if currently muted, enable
    localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = enabled; });
    setAudioMuted(!enabled);
  };
  const toggleVideo = () => {
    if (!localStreamRef.current) return;
    const enabled = videoOff;
    localStreamRef.current.getVideoTracks().forEach((t) => { t.enabled = enabled; });
    setVideoOff(!enabled);
    if (localSelfRef.current) localSelfRef.current.hasVideo = enabled;
  };

  const deleteRoom = useCallback(async (roomId) => {
    try {
      await axios.delete(`${API_URL}/video-rooms/${roomId}`, { headers: authHeaders() });
      setDeletingRoomId(null);
      loadUpcoming();
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not delete the room. Try again.');
      setDeletingRoomId(null);
    }
  }, [authHeaders, loadUpcoming]);

  const copyJoinLink = async (slug) => {
    const url = `${window.location.origin}/join/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(''), 1800);
    } catch { /* ignore */ }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  // Active call
  if (callState === 'connected' && activeRoom) {
    const remoteTiles = Object.values(remotes);
    const tiles = [localSelfRef.current, ...remoteTiles].filter(Boolean);
    return (
      <div className={`h-full flex flex-col ${text}`}>
        {/* header */}
        <div className={`flex items-center justify-between mb-3 flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"/>
              <span className={`text-xs font-bold uppercase tracking-widest ${isDark ? 'text-red-400' : 'text-red-500'}`}>Live</span>
            </div>
            <span className={`text-sm ${isDark ? 'text-white/20' : 'text-slate-200'}`}>·</span>
            <p className={`text-base font-semibold ${text}`}>{activeRoom.title || 'Video call'}</p>
          </div>
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={() => copyJoinLink(activeRoom.slug)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition ${
              isDark ? 'bg-white/8 hover:bg-white/12 text-white/60 border-white/10' : 'bg-slate-100 hover:bg-slate-200 text-slate-600 border-slate-200'
            }`}
            title="Copy shareable join link"
          >
            <LinkIcon className="h-3.5 w-3.5" />
            {copiedSlug === activeRoom.slug ? 'Copied!' : 'Invite'}
          </motion.button>
        </div>

        {/* grid */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className={`grid gap-3 ${gridClasses(tiles.length)}`}>
            {tiles.map((p, i) => (
              <ParticipantTile
                key={p.id || `p-${i}`}
                id={p.id}
                label={p.label || 'Participant'}
                mediaStream={p.mediaStream}
                hasVideo={p.hasVideo}
                isDark={isDark}
                isLocal={p.id === 'self'}
              />
            ))}
          </div>
        </div>

        {/* controls */}
        <div className={`flex-shrink-0 flex items-center justify-center gap-4 mt-3 px-8 py-3 rounded-2xl border backdrop-blur-sm ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/90 border-slate-200 shadow-sm'}`}>
          <motion.button whileTap={{ scale: 0.9 }} onClick={toggleAudio}
            title={audioMuted ? 'Unmute' : 'Mute'}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition border ${
              audioMuted ? 'bg-red-500/20 border-red-500/40 text-red-400'
                         : isDark ? 'bg-white/10 border-white/15 text-white hover:bg-white/15' : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
            }`}>
            {audioMuted ? <MicOffIcon className="h-5 w-5"/> : <MicIcon className="h-5 w-5"/>}
          </motion.button>
          <motion.button whileTap={{ scale: 0.9 }} onClick={leaveRoom}
            title="End call"
            className="w-14 h-14 rounded-full flex items-center justify-center bg-red-600 hover:bg-red-700 text-white transition shadow-lg">
            <PhoneOffIcon className="h-6 w-6"/>
          </motion.button>
          <motion.button whileTap={{ scale: 0.9 }} onClick={toggleVideo}
            title={videoOff ? 'Enable camera' : 'Disable camera'}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition border ${
              videoOff ? 'bg-red-500/20 border-red-500/40 text-red-400'
                       : isDark ? 'bg-white/10 border-white/15 text-white hover:bg-white/15' : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
            }`}>
            {videoOff ? <VideoOffIcon className="h-5 w-5"/> : <VideoIcon className="h-5 w-5"/>}
          </motion.button>
        </div>
      </div>
    );
  }

  // Calling (pending target to answer)
  if (callState === 'calling' && pendingInvite) {
    return (
      <div className={`h-full flex flex-col items-center justify-center ${text}`}>
        <Avatar label={pendingInvite.toEmail} size="lg" isDark={isDark} />
        <p className={`mt-4 text-sm ${muted}`}>Calling…</p>
        <p className="text-lg font-semibold">{pendingInvite.toEmail}</p>
        <div className="flex gap-1 mt-2">
          {[0, 0.2, 0.4].map((d) => (
            <span key={d} className="w-2 h-2 rounded-full bg-white/40 animate-bounce"
              style={{ animationDelay: `${d}s` }} />
          ))}
        </div>
        <motion.button whileTap={{ scale: 0.95 }} onClick={cancelPending}
          className="mt-8 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium">
          <PhoneOffIcon className="h-4 w-4" /> Cancel
        </motion.button>
      </div>
    );
  }

  // Idle / incoming
  return (
    <div className={`h-full flex flex-col ${text}`}>
      {/* Incoming invite overlay */}
      <AnimatePresence>
        {callState === 'incoming' && incomingInvite && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xl rounded-[32px]"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
              className={`rounded-3xl border p-8 w-80 text-center shadow-2xl ${isDark ? 'bg-[#0d1117] border-white/15' : 'bg-white border-slate-200'}`}
            >
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <Avatar label={incomingInvite.fromEmail} size="lg" isDark={isDark} />
                  <span className="absolute -top-1 -right-1 flex h-4 w-4">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"/>
                    <span className="relative inline-flex h-4 w-4 rounded-full bg-green-500"/>
                  </span>
                </div>
              </div>
              <p className={`text-sm font-medium mb-1 ${muted}`}>Incoming video call</p>
              <p className="text-lg font-semibold mb-1">{incomingInvite.fromEmail}</p>
              {incomingInvite.title && <p className={`text-xs mb-5 ${muted}`}>{incomingInvite.title}</p>}
              <div className="flex gap-3">
                <motion.button whileTap={{ scale: 0.95 }} onClick={declineInvite}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/25 font-medium">
                  <PhoneOffIcon className="h-4 w-4"/> Decline
                </motion.button>
                <motion.button whileTap={{ scale: 0.95 }} onClick={acceptInvite}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/25 font-medium">
                  <VideoIcon className="h-4 w-4"/> Answer
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-shrink-0">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold tracking-tight">Video Call</h2>
          <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${
            socketConnected
              ? isDark ? 'bg-green-500/10 border-green-500/25 text-green-400' : 'bg-green-50 border-green-200 text-green-700'
              : isDark ? 'bg-white/5 border-white/10 text-white/40' : 'bg-slate-50 border-slate-200 text-slate-400'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${socketConnected ? 'bg-green-500 animate-pulse' : 'bg-white/20'}`}/>
            {socketConnected ? 'Online' : 'Connecting…'}
          </span>
          {cameraStatus && (
            <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${
              cameraStatus === 'checking'
                ? isDark ? 'bg-white/5 border-white/10 text-white/40' : 'bg-slate-50 border-slate-200 text-slate-400'
                : cameraStatus === 'ok'
                  ? isDark ? 'bg-green-500/10 border-green-500/25 text-green-400' : 'bg-green-50 border-green-200 text-green-700'
                  : isDark ? 'bg-red-500/15 border-red-500/30 text-red-400' : 'bg-red-50 border-red-200 text-red-600'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${cameraStatus === 'ok' ? 'bg-green-500' : cameraStatus === 'checking' ? 'bg-white/20' : 'bg-red-500'}`}/>
              {cameraStatus === 'checking' ? 'Checking camera…' : cameraStatus === 'ok' ? 'Camera OK' : 'Camera: ' + cameraStatus}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <motion.button whileTap={{ scale: 0.95 }} onClick={() => socketRef.current?.emit('get-online-users')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium border transition ${
              isDark ? 'bg-white/10 hover:bg-white/15 text-white border-white/15' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'
            }`}>
            <RefreshIcon className="h-3.5 w-3.5"/> Refresh
          </motion.button>
          <motion.button whileTap={{ scale: 0.95 }}
            onClick={startInstantRoom}
            disabled={!socketConnected}
            title="Start a video call and share the join link"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition disabled:opacity-40 ${
              isDark ? 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/25' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
            }`}>
            <VideoIcon className="h-3.5 w-3.5"/> New video call
          </motion.button>
          <motion.button whileTap={{ scale: 0.95 }}
            onClick={() => { setScheduleError(''); setShowScheduleModal(true); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition ${
              isDark ? 'bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-500/25' : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}>
            <PlusIcon className="h-3.5 w-3.5"/> Schedule Call
          </motion.button>
        </div>
      </div>

      {/* Error */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            className={`border px-4 py-2.5 rounded-xl text-sm mb-4 flex-shrink-0 ${
              isDark ? 'bg-red-500/15 border-red-500/30 text-red-400' : 'bg-red-50 border-red-300 text-red-700'
            }`}
          >
            {error}
            <button onClick={() => setError('')} className="ml-2 opacity-60 hover:opacity-100">✕</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Body — online users (left) + upcoming (right) */}
      <div className="flex-1 grid gap-4 min-h-0 md:grid-cols-3">
        {/* Online users */}
        <div className={`md:col-span-2 flex flex-col min-h-0 rounded-2xl border p-5 ${surface} ${border}`}>
          <div className="flex items-center gap-2 mb-4">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${socketConnected ? 'bg-green-500' : isDark ? 'bg-white/20' : 'bg-slate-300'}`}/>
            <p className={`text-sm font-semibold ${text}`}>
              {onlineUsers.length === 0 ? 'No one online' : `${onlineUsers.length} online now`}
            </p>
          </div>
          <div className="flex-1 overflow-auto">
            {onlineUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-5 py-10">
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${isDark ? 'bg-white/5 border border-white/8' : 'bg-slate-100 border border-slate-200'}`}>
                  <VideoIcon className={`h-9 w-9 ${isDark ? 'text-white/15' : 'text-slate-300'}`}/>
                </div>
                <div className="text-center">
                  <p className={`font-semibold ${text}`}>No one else online</p>
                  <p className={`text-sm mt-1.5 max-w-xs ${muted}`}>
                    {socketConnected ? 'Start a room and share the invite link to bring someone in.' : 'Connecting…'}
                  </p>
                </div>
                {socketConnected && (
                  <motion.button
                    whileTap={{ scale: 0.96 }}
                    onClick={startInstantRoom}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                      isDark ? 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/25' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    }`}>
                    <VideoIcon className="h-4 w-4"/> Start a call
                  </motion.button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {onlineUsers.map((u, i) => (
                  <motion.div
                    key={u.userId}
                    initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                    className={`flex items-center justify-between p-3 rounded-2xl border ${
                      isDark ? 'bg-white/[0.04] border-white/8 hover:bg-white/[0.07]' : 'bg-white border-slate-200 hover:bg-slate-50'
                    } transition`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar label={u.email} size="sm" isDark={isDark} />
                      <div>
                        <p className={`text-sm font-medium ${text}`}>{u.email}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500"/>
                          <span className={`text-xs ${muted}`}>Available</span>
                        </div>
                      </div>
                    </div>
                    <motion.button whileTap={{ scale: 0.95 }}
                      onClick={() => startInstantCall(u.userId, u.email)}
                      disabled={!socketConnected}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition disabled:opacity-40 ${
                        isDark ? 'bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border-sky-500/25' : 'bg-blue-600 hover:bg-blue-700 text-white border-transparent'
                      }`}>
                      <VideoIcon className="h-3.5 w-3.5"/> Call
                    </motion.button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming calls */}
        <div className={`flex flex-col min-h-0 rounded-2xl border p-5 ${surface} ${border}`}>
          <p className={`text-sm font-semibold mb-4 ${text}`}>Upcoming</p>
          <div className="flex-1 overflow-auto">
            {upcoming.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-4 py-10">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${isDark ? 'bg-white/5 border border-white/8' : 'bg-slate-100 border border-slate-200'}`}>
                  <CalIcon className={`h-7 w-7 ${isDark ? 'text-white/15' : 'text-slate-300'}`}/>
                </div>
                <div className="text-center">
                  <p className={`text-sm font-medium ${text}`}>No scheduled calls</p>
                  <p className={`text-xs mt-1 ${faint}`}>Pick a time and share the link</p>
                </div>
                <motion.button
                  whileTap={{ scale: 0.96 }}
                  onClick={() => { setScheduleError(''); setShowScheduleModal(true); }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition ${
                    isDark ? 'bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-500/25' : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}>
                  <PlusIcon className="h-3.5 w-3.5"/> Schedule a call
                </motion.button>
              </div>
            ) : (
              <div className="space-y-2">
                {upcoming.map((r) => {
                  const minsUntil = r.scheduledAt
                    ? Math.floor((new Date(r.scheduledAt).getTime() - Date.now()) / 60000)
                    : null;
                  const joinActive = minsUntil === null || minsUntil <= 5;
                  return (
                    <div key={r.id} className={`p-3 rounded-xl border ${isDark ? 'bg-white/[0.04] border-white/8' : 'bg-white border-slate-200'}`}>
                      <p className={`text-sm font-medium ${text} truncate`}>{r.title || 'Untitled'}</p>
                      <p className={`text-xs ${muted} mt-0.5`}>
                        {r.scheduledAt
                          ? (minsUntil !== null && minsUntil <= 60 && minsUntil > 0
                              ? `In ${minsUntil} min`
                              : new Date(r.scheduledAt).toLocaleString())
                          : 'Instant room'}
                      </p>
                      {deletingRoomId === r.id ? (
                        <div className={`flex items-center gap-2 mt-2 p-2 rounded-lg border ${isDark ? 'bg-red-500/10 border-red-500/25' : 'bg-red-50 border-red-200'}`}>
                          <span className={`text-xs flex-1 ${isDark ? 'text-red-300' : 'text-red-600'}`}>Delete this room?</span>
                          <button
                            onClick={() => deleteRoom(r.id)}
                            className="px-2.5 py-1 rounded-md text-xs bg-red-500 text-white hover:bg-red-600 transition"
                          >Yes</button>
                          <button
                            onClick={() => setDeletingRoomId(null)}
                            className={`px-2.5 py-1 rounded-md text-xs border transition ${isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                          >Cancel</button>
                        </div>
                      ) : (
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => copyJoinLink(r.slug)}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border ${
                              isDark ? 'bg-white/10 border-white/15 text-white hover:bg-white/15' : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                            }`}>
                            <LinkIcon className="h-3 w-3"/>
                            {copiedSlug === r.slug ? 'Copied!' : 'Copy link'}
                          </button>
                          <button
                            onClick={() => joinScheduledRoom(r.slug, r.title, r.id)}
                            disabled={!joinActive}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border transition disabled:opacity-35 disabled:cursor-not-allowed ${
                              isDark
                                ? 'bg-emerald-500/20 border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/30'
                                : 'bg-emerald-600 border-transparent text-white hover:bg-emerald-700'
                            }`}
                            title={joinActive ? 'Join this call' : `Available ${minsUntil} min before start`}
                          >
                            <VideoIcon className="h-3 w-3"/>
                            Join
                          </button>
                          <button
                            onClick={() => setDeletingRoomId(r.id)}
                            className={`ml-auto flex items-center justify-center w-7 h-7 rounded-lg border transition ${
                              isDark ? 'border-white/15 text-red-400 hover:bg-red-500/15 hover:border-red-500/30' : 'border-slate-200 text-red-400 hover:bg-red-50 hover:border-red-200'
                            }`}
                            title="Delete this room"
                          >
                            <TrashIcon className="h-3 w-3"/>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={`flex-shrink-0 mt-4 pt-4 border-t ${border}`}>
        <div className="flex items-center gap-2">
          <UserIcon className={`h-4 w-4 ${faint}`}/>
          <span className={`text-xs ${muted}`}>Signed in as</span>
          <span className={`text-xs font-medium ${text}`}>{user?.email}</span>
        </div>
      </div>

      {/* ── Schedule Call modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {showScheduleModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xl rounded-[32px]"
            onClick={(e) => { if (e.target === e.currentTarget) setShowScheduleModal(false); }}
          >
            <motion.div
              initial={{ scale: 0.93, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 16 }}
              className={`rounded-3xl border p-7 w-full max-w-sm shadow-2xl ${isDark ? 'bg-[#0d1117] border-white/15' : 'bg-white border-slate-200'}`}
            >
              <h3 className={`text-lg font-semibold mb-5 ${text}`}>Schedule a Call</h3>

              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className={`block text-xs font-medium mb-1.5 ${muted}`}>Title <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    value={scheduleForm.title}
                    onChange={(e) => setScheduleForm((f) => ({ ...f, title: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') scheduleCall(); }}
                    placeholder="Team sync, demo, etc."
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                      isDark
                        ? 'bg-white/5 border-white/10 text-white placeholder-white/30 focus:ring-sky-500/40'
                        : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-blue-500/40'
                    }`}
                    autoFocus
                  />
                </div>

                {/* Date + time */}
                <div>
                  <label className={`block text-xs font-medium mb-1.5 ${muted}`}>Date & Time <span className="text-red-400">*</span></label>
                  <input
                    type="datetime-local"
                    value={scheduleForm.scheduledAt}
                    min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                    onChange={(e) => setScheduleForm((f) => ({ ...f, scheduledAt: e.target.value }))}
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                      isDark
                        ? 'bg-white/5 border-white/10 text-white focus:ring-sky-500/40 [color-scheme:dark]'
                        : 'bg-slate-50 border-slate-200 text-slate-900 focus:ring-blue-500/40'
                    }`}
                  />
                </div>

                {/* Participant emails */}
                <div>
                  <label className={`block text-xs font-medium mb-1.5 ${muted}`}>Invite by email <span className={faint}>(optional, comma-separated)</span></label>
                  <input
                    type="text"
                    value={scheduleForm.emails}
                    onChange={(e) => setScheduleForm((f) => ({ ...f, emails: e.target.value }))}
                    placeholder="alice@example.com, bob@example.com"
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                      isDark
                        ? 'bg-white/5 border-white/10 text-white placeholder-white/30 focus:ring-sky-500/40'
                        : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-blue-500/40'
                    }`}
                  />
                </div>

                {/* Max participants */}
                <div>
                  <label className={`block text-xs font-medium mb-1.5 ${muted}`}>Max participants</label>
                  <input
                    type="number"
                    min={2} max={10}
                    value={scheduleForm.maxParticipants}
                    onChange={(e) => setScheduleForm((f) => ({ ...f, maxParticipants: e.target.value }))}
                    className={`w-24 rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                      isDark
                        ? 'bg-white/5 border-white/10 text-white focus:ring-sky-500/40'
                        : 'bg-slate-50 border-slate-200 text-slate-900 focus:ring-blue-500/40'
                    }`}
                  />
                </div>
              </div>

              {/* Error */}
              {scheduleError && (
                <p className="mt-3 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  {scheduleError}
                </p>
              )}

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className={`flex-1 py-2.5 rounded-2xl text-sm font-medium border transition ${
                    isDark ? 'border-white/15 text-white/60 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={scheduleCall}
                  disabled={scheduleLoading}
                  className="flex-1 py-2.5 rounded-2xl bg-sky-500 hover:bg-sky-400 disabled:bg-sky-500/50 disabled:cursor-not-allowed text-white text-sm font-semibold transition"
                >
                  {scheduleLoading ? 'Scheduling…' : 'Schedule'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
